import {
  SOCIAL_PLATFORMS,
  SUPPORT_CONTACTS,
} from '@/shared/constants/externalLinks';
import type { ExternalLinkOptions } from './types';

export const WHATSAPP_FALLBACK_DELAY_MS = 900;

type WhatsAppLaunchEnvironment = 'desktop' | 'ios' | 'android';

type NavigatorLike = Pick<Navigator, 'userAgent' | 'vendor' | 'maxTouchPoints'> & {
  userAgentData?: {
    mobile?: boolean;
    platform?: string;
  };
};

interface OpenWhatsAppMobileLaunchOptions {
  primaryUrl: string;
  fallbackUrl: string;
  win?: Window;
  doc?: Document;
  delayMs?: number;
}

interface OpenBettyChatLaunchOptions {
  phone?: string;
  message?: string;
  win?: Window;
  doc?: Document;
  nav?: NavigatorLike;
  delayMs?: number;
  externalLinkOptions?: ExternalLinkOptions;
}

function normalizePhone(phone: string): string {
  return phone.replace(/[^\d]/g, '');
}

function isDocumentStillVisible(doc: Document): boolean {
  return doc.visibilityState !== 'hidden' && doc.hidden !== true;
}

function safeWindowOpen(
  win: Window,
  url: string,
  options: ExternalLinkOptions = {}
): void {
  const { target = '_blank', windowFeatures = '' } = options;

  try {
    win.open(url, target, windowFeatures);
  } catch {
    if (target === '_self') {
      win.location.href = url;
    }
  }
}

export function buildWhatsAppDeepLink(
  phone: string = SUPPORT_CONTACTS.BETTY_WHATSAPP,
  message: string = SUPPORT_CONTACTS.BETTY_DEFAULT_MESSAGE
): string {
  const params = new URLSearchParams({
    phone: normalizePhone(phone),
    text: message,
  });

  return `whatsapp://send?${params.toString()}`;
}

export function buildWhatsAppWebUrl(
  phone: string = SUPPORT_CONTACTS.BETTY_WHATSAPP,
  message: string = SUPPORT_CONTACTS.BETTY_DEFAULT_MESSAGE
): string {
  return SOCIAL_PLATFORMS.WHATSAPP.chatUrl(phone, message);
}

export function buildWhatsAppIntentUrl(
  phone: string = SUPPORT_CONTACTS.BETTY_WHATSAPP,
  message: string = SUPPORT_CONTACTS.BETTY_DEFAULT_MESSAGE,
  fallbackUrl: string = buildWhatsAppWebUrl(phone, message)
): string {
  const params = new URLSearchParams({
    phone: normalizePhone(phone),
    text: message,
  });

  return `intent://send?${params.toString()}#Intent;scheme=whatsapp;package=com.whatsapp;S.browser_fallback_url=${encodeURIComponent(
    fallbackUrl
  )};end`;
}

export function resolveWhatsAppLaunchEnvironment(
  nav?: NavigatorLike
): WhatsAppLaunchEnvironment {
  if (!nav) {
    return 'desktop';
  }

  const userAgent = (nav.userAgent || nav.vendor || '').toLowerCase();
  const platform = (nav.userAgentData?.platform || '').toLowerCase();
  const maxTouchPoints = nav.maxTouchPoints || 0;

  const isAndroid =
    userAgent.includes('android') || platform.includes('android');
  if (isAndroid) {
    return 'android';
  }

  const isIOS =
    /(iphone|ipad|ipod)/i.test(userAgent) ||
    platform.includes('ios') ||
    (userAgent.includes('macintosh') && maxTouchPoints > 1);

  if (isIOS) {
    return 'ios';
  }

  return 'desktop';
}

export function openWhatsAppMobileLaunch(
  options: OpenWhatsAppMobileLaunchOptions
): void {
  const win = options.win ?? (typeof window !== 'undefined' ? window : undefined);
  const doc = options.doc ?? (typeof document !== 'undefined' ? document : undefined);

  if (!win) {
    return;
  }

  if (!doc) {
    win.location.href = options.primaryUrl;
    return;
  }

  const delayMs = options.delayMs ?? WHATSAPP_FALLBACK_DELAY_MS;
  let didLeavePage = false;

  const handleVisibilityChange = () => {
    if (!isDocumentStillVisible(doc)) {
      didLeavePage = true;
    }
  };

  const fallbackToWeb = () => {
    if (typeof win.location.assign === 'function') {
      win.location.assign(options.fallbackUrl);
      return;
    }

    win.location.href = options.fallbackUrl;
  };

  try {
    doc.addEventListener('visibilitychange', handleVisibilityChange);
    win.location.href = options.primaryUrl;
  } catch {
    doc.removeEventListener('visibilitychange', handleVisibilityChange);
    fallbackToWeb();
    return;
  }

  win.setTimeout(() => {
    doc.removeEventListener('visibilitychange', handleVisibilityChange);

    if (!didLeavePage && isDocumentStillVisible(doc)) {
      fallbackToWeb();
    }
  }, delayMs);
}

export function openBettyChatWithLaunchStrategy(
  options: OpenBettyChatLaunchOptions = {}
): void {
  const win = options.win ?? (typeof window !== 'undefined' ? window : undefined);
  if (!win) {
    return;
  }

  const phone = options.phone ?? SUPPORT_CONTACTS.BETTY_WHATSAPP;
  const message = options.message ?? SUPPORT_CONTACTS.BETTY_DEFAULT_MESSAGE;
  const fallbackUrl = buildWhatsAppWebUrl(phone, message);
  const environment = resolveWhatsAppLaunchEnvironment(
    options.nav ??
      (typeof navigator !== 'undefined' ? (navigator as NavigatorLike) : undefined)
  );

  if (environment === 'desktop') {
    safeWindowOpen(win, fallbackUrl, options.externalLinkOptions);
    return;
  }

  const primaryUrl =
    environment === 'ios'
      ? buildWhatsAppDeepLink(phone, message)
      : buildWhatsAppIntentUrl(phone, message, fallbackUrl);

  openWhatsAppMobileLaunch({
    primaryUrl,
    fallbackUrl,
    win,
    doc: options.doc,
    delayMs: options.delayMs,
  });
}
